import { FC, ReactNode, useState } from 'react'
import {
  FormControl,
  MenuItem,
  Box,
  SelectProps,
  Select as MUISelect,
  FormHelperText,
  Typography
} from '@material-ui/core'
import { SelectInputProps } from '@material-ui/core/Select/SelectInput'
import styled from 'styled-components'
import { KeyboardArrowDown } from '@material-ui/icons'
import { darken } from 'polished'
import { FormLabel } from 'common/atoms/FormLabel'
import { RequiredSign } from 'pages/content/components/jobs/components/atoms'

interface ISelectProps extends SelectProps {
  menuItemList: any[]
  theme: any
  width?: string
  label?: string | ReactNode
  placeholder?: string
  defaultValue?: string | number
  style?: any
  id?: string
  required?: boolean
  infoText?: string
  selectValue?: any
  design?: 'small'
  useCustomOpenFnc?: boolean
  useCustomCloseFnc?: boolean
  menuItemWidth?: string
}

const StyledSelect = styled(MUISelect)`
  && {
    background-color: ${props => props.theme.colors.bgPrimary};
    border: 1px solid transparent;
    border-radius: 0.75rem;
  }

  &&.select-icon {
    color: ${props => props.theme.colors.accent} !important;
    transform: rotate(180deg);

    transition-property: color transform;
    transition-duration: 0.25s;
  }

  &&:has([aria-expanded='true']) {
    border-color: ${props => props.theme.colors.primary} !important;

    .select-icon {
      color: ${props => props.theme.colors.primary} !important;
      transform: rotate(180deg);
    }
  }

  && ul {
    min-width: 250px;
  }
`

const StyledInfoText = styled(Typography)`
  && {
    font-size: 0.625rem;
    font-style: normal;
    font-weight: 500;
    line-height: 166%; /* 1.0375rem */
    letter-spacing: 0.025rem;
    color: ${({ theme }) => theme.colors.accent};
  }
`

const StyledFormControl = styled(FormControl)<{ width?: string; disabled?: boolean; small?: boolean; design?: string }>`
  max-width: ${props => props.width ?? '5.4rem'};
  width: 100%;
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};

  & .MuiInput-root {
    height: ${props => props.design === 'small' ? '32px' : ''};
  }
`

export const Select: FC<ISelectProps> = props => {
  const [isOpen, setIsOpen] = useState(false)
  const {
    menuItemList,
    theme,
    width,
    label,
    placeholder,
    defaultValue,
    style,
    id,
    required,
    infoText,
    disabled,
    selectValue,
    design,
    onOpen,
    onClose,
    useCustomOpenFnc,
    useCustomCloseFnc,
    menuItemWidth = '250px',
    ...rest
  } = props

  const selectDefaultValue = () => {
    if (defaultValue) {
      return defaultValue
    } else if (placeholder) {
      return placeholder
    } else return 'none'
  }

  return (
    <StyledFormControl design={design} width={width} style={style} disabled={disabled} className="obsidi-select-molecule">
      {label && (
        <FormLabel style={{ marginLeft: '0.8em' }}>
          {label}
          {<RequiredSign visible={required} />}
        </FormLabel>
      )}
      <StyledSelect
        disableUnderline
        displayEmpty
        IconComponent={props => (
          <KeyboardArrowDown
            {...props}
            className="select-icon"
            style={{ color: theme.colors.accent, marginRight: '6px', cursor: 'pointer' }}
            onClick={(evt) => {
              if (onOpen && useCustomOpenFnc) {
                onOpen(evt)
              } else {
                setIsOpen(prev => !prev)
              }
            }}
          />
        )}
        defaultValue={selectDefaultValue()}
        onOpen={(evt) => {
          if (useCustomOpenFnc) {
            onOpen?.(evt)
            return;
          }
          setIsOpen(true)
        }}
        onClose={(evt) => {
          if (useCustomCloseFnc) {
            onClose?.(evt)
            return;
          }
          setIsOpen(false)
        }}
        open={isOpen}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
          getContentAnchorEl: null,
          style: {
            width: menuItemWidth
          },
        }}
        {...rest}
      >
        {placeholder && !rest.value && (
          <MenuItem disabled value="">
            <Box color={darken(0.3, theme.colors.accent)} fontWeight="400">
              <Typography variant="body2">{placeholder}</Typography>
            </Box>
          </MenuItem>
        )}
        {menuItemList?.map(menuItem => (
          <MenuItem value={menuItem.value}>{menuItem.label}</MenuItem>
        ))}
      </StyledSelect>
      <FormHelperText>{infoText && <StyledInfoText variant="caption">{infoText}</StyledInfoText>}</FormHelperText>
    </StyledFormControl>
  )
}
